import React from 'react';
import { AIAnnotationStructuredDataType, CadastralSurveyAIAnnotationCategory, DocumentTypeValue } from '../../../../domain/types';
import { IdentityDocumentType } from '../../../../domain/types/contact';
import { Document, Contact } from '../../../../providers/api/dtos';
import { AIAnnotationContactData, IdentityDocumentData } from '../../../../providers/api/dtos/documentFile';
import { areContactsExactMatch, areContactsPartialMatch } from '../../utils';
import { ContactSuggestion } from '../../../../contact/types';

export interface AiSuggestedSellers {
  suggestedSellers: ContactSuggestion[],
  cadastralSurveySellers: AIAnnotationContactData[],
}

// Merges two plain objects field-by-field: first-found value wins (a[field] ?? b[field]).
const mergeNested = <T extends object>(a: T | undefined, b: T | undefined): T | undefined => {
  if (!a && !b) return undefined;
  if (!a) return b;
  if (!b) return a;
  return ({
    ...b,
    ...Object.fromEntries(Object.entries(a).filter(([, v]) => v !== undefined)),
  }) as T;
};

// Both criteria are evaluated independently: a match on either field alone is sufficient,
// since either a shared fiscal code or a shared name is a reliable signal when the other is absent.
export const isSamePerson = (a: AIAnnotationContactData, b: AIAnnotationContactData): boolean => {
  const fiscalCodeMatch = !!(a.fiscalCode && b.fiscalCode && a.fiscalCode.toLowerCase() === b.fiscalCode.toLowerCase());
  const nameMatch = !!(a.name && b.name && a.name.toLowerCase() === b.name.toLowerCase());
  return fiscalCodeMatch || nameMatch;
};

export const mergeContactData = (
  primarySeller: AIAnnotationContactData,
  secondarySeller: AIAnnotationContactData,
): AIAnnotationContactData => ({
  // primary seller (the cadastral one) takes priority for ownership data
  name: primarySeller.name || secondarySeller.name,
  fiscalCode: primarySeller.fiscalCode || secondarySeller.fiscalCode,
  email: primarySeller.email || secondarySeller.email,
  phoneNumber: primarySeller.phoneNumber || secondarySeller.phoneNumber,
  residence: primarySeller.residence || secondarySeller.residence,
  citizenship: primarySeller.citizenship || secondarySeller.citizenship,

  // secondary seller (the identity document one) takes priority for personal data
  birthDay: secondarySeller.birthDay || primarySeller.birthDay,
  birthPlace: secondarySeller.birthPlace || primarySeller.birthPlace,
  identityDocument: secondarySeller.identityDocument ?? primarySeller.identityDocument,
});

// When called with a single collection: merges entries that refer to the same person within the
// collection, combining their fields with first-found value winning for each field.
// When called with two collections: merges secondary into the primary using priority-based field rules (see mergeContactData).
export const mergeSellers = (
  primary: AIAnnotationContactData[],
  secondary?: AIAnnotationContactData[],
): AIAnnotationContactData[] => {
  if (secondary === undefined) {
    return primary.reduce<AIAnnotationContactData[]>((acc, contact) => {
      const existingIndex = acc.findIndex((c) => isSamePerson(contact, c));
      if (existingIndex === -1) {
        return [...acc, contact];
      }
      const existing = acc[existingIndex];
      const merged: AIAnnotationContactData = {
        name: existing.name || contact.name,
        fiscalCode: existing.fiscalCode || contact.fiscalCode,
        email: existing.email || contact.email,
        phoneNumber: existing.phoneNumber || contact.phoneNumber,
        residence: mergeNested(existing.residence, contact.residence),
        citizenship: existing.citizenship || contact.citizenship,
        birthDay: existing.birthDay || contact.birthDay,
        birthPlace: existing.birthPlace || contact.birthPlace,
        identityDocument: mergeNested(existing.identityDocument, contact.identityDocument),
      };
      return [...acc.slice(0, existingIndex), merged, ...acc.slice(existingIndex + 1)];
    }, []);
  }

  // Tracked by index rather than reference so structurally equal objects are not confused with each other.
  const matchedPrimaryIndices = new Set<number>();

  const mergedSecondary = secondary.map((secondarySeller) => {
    const primaryIndex = primary.findIndex((c) => isSamePerson(secondarySeller, c));

    if (primaryIndex === -1) {
      return secondarySeller;
    }

    matchedPrimaryIndices.add(primaryIndex);

    return mergeContactData(primary[primaryIndex], secondarySeller);
  });

  const unmatchedPrimary = primary.filter((_, index) => !matchedPrimaryIndices.has(index));

  return [...unmatchedPrimary, ...mergedSecondary];
};

export const useAiSuggestedSellers = (
  contacts?: Contact[],
  cadastralSurvey?: Document,
  identityDocument?: Document,
): AiSuggestedSellers => React.useMemo(() => {
  const cadastralSurveySellers: AIAnnotationContactData[] = cadastralSurvey?.type === DocumentTypeValue.CADASTRAL_SURVEY
    ? mergeSellers(
      cadastralSurvey.files
        .flatMap((file) => (file.analysisResult?.annotations ?? [])
          .filter((annotation) => annotation.category === CadastralSurveyAIAnnotationCategory.OWNERSHIP)
          .flatMap((annotation) => annotation.structuredData ?? [])
          .filter((sd) => sd.type === AIAnnotationStructuredDataType.CONTACT)
          .map((sd) => sd.data as AIAnnotationContactData)),
    )
    : [];

  const identityDocumentSellers: AIAnnotationContactData[] = identityDocument?.type === DocumentTypeValue.IDENTITY_DOCUMENT
    ? mergeSellers(
      identityDocument.files
        .flatMap((file) => (file.analysisResult?.annotations ?? [])
          .filter((annotation) => annotation.category === CadastralSurveyAIAnnotationCategory.IDENTITY_DOCUMENT)
          .flatMap((annotation) => annotation.structuredData ?? [])
          .filter((sd) => sd.type === AIAnnotationStructuredDataType.IDENTITY_DOCUMENT)
          .filter((sd) => {
            const data = sd.data as IdentityDocumentData;
            // Strict false check: undefined means the AI did not assess expiry, so we exclude those entries too.
            return data.isExpired === false && (data.anomalies ?? []).length === 0;
          })
          .map((sd) => {
            const data = sd.data as IdentityDocumentData;
            const documentType = data.documentType
              ? IdentityDocumentType[data.documentType.toUpperCase() as keyof typeof IdentityDocumentType]
              : undefined;
            return {
              name: data.holder?.name || undefined,
              birthDay: data.holder?.birthDay,
              birthPlace: data.holder?.birthPlace,
              fiscalCode: data.holder?.fiscalCode,
              citizenship: data.holder?.citizenship,
              residence: data.holder?.residence,
              email: data.holder?.email,
              phoneNumber: data.holder?.phoneNumber,
              // Avoid producing an empty object when the AI returned no document fields.
              identityDocument: (documentType || data.documentNumber || data.issuedBy || data.issuedAt)
                ? {
                  type: documentType,
                  number: data.documentNumber,
                  issuedBy: data.issuedBy,
                  issuedAt: data.issuedAt,
                }
                : undefined,
            } as AIAnnotationContactData;
          })
          .filter((contact) => !!contact.name)),
    )
    : [];

  const allSellers = mergeSellers(cadastralSurveySellers, identityDocumentSellers);
  const suggestedSellers = allSellers.flatMap((suggestion): ContactSuggestion[] => {
    if (contacts?.find((c) => areContactsExactMatch(c, suggestion))) {
      return [];
    }
    const partialMatch = contacts?.find((c) => areContactsPartialMatch(c, suggestion));
    return [{ suggestion, contact: partialMatch }];
  });

  // cadastralSurveySellers is returned separately (not allSellers) so the UI can compare
  // the raw cadastral count against the selected sellers for the mismatch warning.
  return { suggestedSellers, cadastralSurveySellers };
}, [cadastralSurvey?.files, cadastralSurvey?.type, identityDocument?.files, identityDocument?.type, contacts]);
